import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import IconButton from "@cayuse/shared-react.components.icon-button";

import { mapValueToCodes } from "components/flowable/ClassificationCodes/helpers";
import { useCodeValidationContext } from "components/flowable/ClassificationCodes/internal/CodeValidationContext";
import Table from "components/Table";
import Tooltip from "components/Tooltip";
import { useDictionary } from "hooks";

import { CodeTemplateRow } from "./CodeTemplateRow";
import styles from "./styles.css";


const CodeTemplate = ({
	id,
	codeType,
	dictionary,
	minimumNumberOfCodes,
	maximumNumberOfCodes,
	includePercentage,
	toCodeOptions,
	value,
	onChange,
	label,
	isRequired,
	enabled,
	tooltip,
}) => {
	const currentId = useRef(id);
	const { common, errorMessages } = useDictionary("classificationCodes");
	const [ codes, setCodes ] = useState(mapValueToCodes(value, minimumNumberOfCodes));
	const { totalPercentageError, customValidationErrors } = useCodeValidationContext();
	const [ touched, setTouched ] = useState(false);

	const toCodeOptionsWithUsedCodeDisabled = useCallback((options, searchOptions = true) => {
		const codeValues = codes.map(code => code.code);
		const newOptions = searchOptions ? toCodeOptions(options) : options;

		return newOptions.map(option => {
			const selected = codeValues.includes(option.value);

			return {
				...option,
				selected,
				"data-value": option.value,
				"data-text": option.text,
				disabled: selected || option.level !== option.maxlevel,
				content: <div className="text">{option.text}</div>
			};
		});
	}, [ codes, toCodeOptions ]);

	useEffect(() => {
		if (currentId.current !== id || value && value !== JSON.stringify(codes)) {
			currentId.current = id;
			setCodes(mapValueToCodes(value, minimumNumberOfCodes));
		}
	}, [ id, value ]);

	const onAddRow = () => {
		if (codes.length < maximumNumberOfCodes) {
			onChange([ ...codes, { code: "", percentage: "" } ]);
		}
	};

	const onRemoveRow = index => {
		onChange(codes.filter((_, k) => k !== +index));
	};

	const onChangeCodeObject = (index, update) => {
		onChange(codes.map((it, k) => k === +index ? { ...it, ...update } : it));
	};

	return (
		<Table overflow="visible" role="presentation" unstackable className={styles.codeTable} noBorders={true} noContentBorders={true} captionText="Classification Codes">
			<Table.Header>
				<Table.Row>
					<Table.HeaderCell className={styles.lookupColumn}>
						<div className={styles.labelGroup}>
							<div className={styles.labelGroupTitle}>
								<Tooltip tooltipContent={tooltip} id={label}>
									<span data-required={isRequired}>{label}</span>
								</Tooltip>
								{isRequired && <span>{common.required}</span>}
							</div>
						</div>
					</Table.HeaderCell>
					<Table.Cell className={styles.percentColumn}>&nbsp;</Table.Cell>
					<Table.Cell className={styles.buttonColumn}>&nbsp;</Table.Cell>
				</Table.Row>
				<Table.Row>
					<Table.HeaderCell className={styles.lookupColumn}>
						<div className={styles.labelGroup}>
							<span id="classification-code-search-label" className={styles.headerText} data-required={isRequired}>
								{dictionary.searchLabel}
							</span>
						</div>
					</Table.HeaderCell>
					{includePercentage && (
						<Table.HeaderCell className={styles.percentColumn}>
							<div className={styles.labelGroup}>
								<span className={styles.headerText} data-required={isRequired}>{common.percentOfResearch}</span>
							</div>
						</Table.HeaderCell>
					)}
					<Table.Cell className={styles.buttonColumn}>&nbsp;</Table.Cell>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{codes.map((obj, k) => {
					const removeRowIsEnabled = k !== 0 && k === codes.length - 1 && codes.length > minimumNumberOfCodes;

					return (
						<CodeTemplateRow
							key={k}
							{...obj}
							index={k}
							searchLabel={dictionary.searchLabel || "Field of Research"}
							percentOfResearchLabel={common.percentOfResearch}
							removeLabel={common.remove}
							codeType={codeType}
							toCodeOptions={toCodeOptionsWithUsedCodeDisabled}
							required={isRequired}
							disabled={!enabled}
							includePercentage={includePercentage}
							onChangeCodeObject={update => onChangeCodeObject(k, update)}
							onRemoveRow={removeRowIsEnabled ? () => onRemoveRow(k) : undefined}
							setTouched={setTouched}
						/>
					);
				})}
				<Table.Row className={styles.codeFieldsColumnWrapper}>
					<Table.Cell className={styles.codeFieldsColumn}>
						<div className={styles.codeFieldsActions}>
							<IconButton
								className={styles.addButton}
								icon="plus"
								labelPosition="left"
								primary
								onClick={onAddRow}
								disabled={!enabled || codes.length === maximumNumberOfCodes}
							>{dictionary.addLabel}
							</IconButton>
							{codes.length === maximumNumberOfCodes && (
								<span className={styles.maximumNumberOfCodes}>
									{errorMessages.maximumReached}
								</span>
							)}
						</div>
					</Table.Cell>
					<Table.Cell className={styles.codeFieldsErrorColumn}>
						<div className={styles.codeFieldsActions}>
							{!!totalPercentageError && (
								<span className={styles.error}>{totalPercentageError}</span>
							)}
						</div>
					</Table.Cell>
				</Table.Row>
				<Table.Row>
					<Table.Cell className={styles.codeFieldsCustomValidationColumn}>
						<div className={styles.errorContainer}>
							{touched && customValidationErrors.map((error, index) => (
								<span key={index} className={styles.error}>
									{error}
								</span>
							))}
						</div>
					</Table.Cell>
				</Table.Row>
			</Table.Body>
		</Table>
	);
};

CodeTemplate.defaultProps = {
	dictionary: {},
};

CodeTemplate.propTypes = {
	codeType: PropTypes.oneOf([ "for", "herdc", "seo", "toa" ]),
	dictionary: PropTypes.shape({
		searchLabel: PropTypes.string,
		addLabel: PropTypes.string,
	}),
	minimumNumberOfCodes: PropTypes.number.isRequired,
	maximumNumberOfCodes: PropTypes.number.isRequired,
	includePercentage: PropTypes.bool.isRequired,
	toCodeOptions: PropTypes.func.isRequired,
	value: PropTypes.any,
	label: PropTypes.string,
	isRequired: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	enabled: PropTypes.bool.isRequired,
};

export default CodeTemplate;
